import { useState } from 'react';
import { Loader2, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const ELIGIBILITY_STYLE = {
  would_push: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cross_entity_blocked: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  possible_conflict: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function eligibilityBadgeClass(eligibility) {
  if (ELIGIBILITY_STYLE[eligibility]) return ELIGIBILITY_STYLE[eligibility];
  if (eligibility?.startsWith('would_skip:')) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
  }
  return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
}

export function PennylanePushDiagnosticsPanel() {
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  // Card-honesty: a Preview/Apply that errors must show the error ON THE
  // CARD, never silently no-op -- a toast alone can be missed or auto-
  // dismissed, and this card previously had no persistent error state at
  // all, which read identically to "the click did nothing" (CLAUDE.md
  // section 8's card-honesty rule, already enforced elsewhere e.g.
  // components/admin/EBCoveragePanel.jsx).
  const [error, setError] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setApplied(null);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.pennylanePushDiagnosticsPreview();
      setPreview(data);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.pennylanePushDiagnosticsApply();
      setApplied(data);
      toast.success(
        `${data.synced ?? 0} synced, ${data.bank_matched ?? 0} bank-matched, ` +
        `${data.cross_entity_blocked ?? 0} cross-entity blocked, ${data.conflict ?? 0} conflicts`
      );
      // Re-preview so the card reflects what just drained rather than a
      // stale pre-apply population.
      const { data: refreshed } = await diagnosticsAPI.pennylanePushDiagnosticsPreview();
      setPreview(refreshed);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Push Reconciliations to Pennylane</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Preview is read-only and makes no live Pennylane calls -- eligibility is predicted
          from locally-stored state. Apply triggers the real push
          (services/pennylane_push_retry.py::run_pennylane_push_retry) on demand, out of band
          from its normal 6h schedule -- every write still goes through that function's own
          guards (cross-entity block, conflict guard, credential resolution). Idempotent --
          re-Apply only acts on what's still eligible.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button data-testid="pennylane-push-diag-preview" size="sm" variant="outline" onClick={runPreview} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && preview.count > 0 && (
            <Button data-testid="pennylane-push-diag-apply" size="sm" onClick={runApply} disabled={loading}>
              Apply
            </Button>
          )}
        </div>

        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-red-500" data-testid="pennylane-push-diag-error">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {preview && preview.count === 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="pennylane-push-diag-empty">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            {preview.note || 'No invoices currently eligible for a Pennylane push.'}
          </div>
        )}

        {preview && preview.count > 0 && (
          <div className="text-xs space-y-2" data-testid="pennylane-push-diag-preview-result">
            <div className="text-muted-foreground">{preview.note}</div>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(preview.by_eligibility).map(([elig, n]) => (
                <Badge key={elig} variant="secondary" className={cn('text-xs', eligibilityBadgeClass(elig))}>
                  {elig}: {n}
                </Badge>
              ))}
            </div>
            <table className="w-full text-xs border-collapse" data-testid="pennylane-push-diag-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-1 pr-3">Invoice</th>
                  <th className="py-1 pr-3">Entity</th>
                  <th className="py-1 pr-3">Stage</th>
                  <th className="py-1 pr-3">Eligibility</th>
                  <th className="py-1 pr-3">Cached Pennylane state</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={`${row.invoice_id}-${row.stage}`} className="border-b border-muted/50">
                    <td className="py-1 pr-3">{row.invoice_number || row.invoice_id}</td>
                    <td className="py-1 pr-3">{row.entity_id}</td>
                    <td className="py-1 pr-3">{row.stage}</td>
                    <td className="py-1 pr-3">
                      <Badge variant="secondary" className={cn('text-xs', eligibilityBadgeClass(row.eligibility))}>
                        {row.eligibility}
                      </Badge>
                    </td>
                    <td className="py-1 pr-3">
                      {row.cached_accounting_reconciliation
                        ? `reconciled=${String(!!row.cached_accounting_reconciliation.reconciled)}, counterpart=${row.cached_accounting_reconciliation.counterpart_invoice_id ?? '-'}`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {applied && (
          <div className="text-xs space-y-1" data-testid="pennylane-push-diag-apply-result">
            <div>
              synced={applied.synced ?? 0}, bank_matched={applied.bank_matched ?? 0}, failed={applied.failed ?? 0},
              {' '}refused={applied.refused ?? 0}, blocked_no_credential={applied.blocked_no_credential ?? 0},
              {' '}cross_entity_blocked={applied.cross_entity_blocked ?? 0}, conflict={applied.conflict ?? 0}
            </div>
            {applied.conflict_invoices?.length > 0 && (
              <div className="text-red-600 dark:text-red-400">
                Conflicts: {applied.conflict_invoices.map((c) => c.invoice_number || c.id).join(', ')} --
                already bank-matched to a different Pennylane transaction, see Reconciliation Disagreements.
              </div>
            )}
            {applied.cross_entity_blocked_invoices?.length > 0 && (
              <div className="text-orange-600 dark:text-orange-400">
                Cross-entity blocked: {applied.cross_entity_blocked_invoices.map((c) => c.invoice_number || c.id).join(', ')}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
